// What one offer sells for and what it costs, worked out in one place so everything that
// reports a quote's profit reports the same profit.
//
// The financial summary bar on the opportunity and the opportunities table each used to
// work this out themselves, and drifted apart: the table converted at today's exchange
// rates even when the quote carried the rates it was priced at, and counted fields the
// customer never sees. The two figures then disagreed for the same quote.

import _ from 'lodash';

import { calcOfferSum } from './opportunity.js';
import { getTotalRates } from '../dashboard/utils/rates.js';

// A field parked in the UNASSIGNED bucket that the preface never references is hidden from
// the builder and stripped from the customer-facing quote server-side. It must not reach a
// total either.
function isDisconnectedField({ section, field, preface }) {
    if (section?.title !== '***UNASSIGNED***') return false;
    if (!field?.id) return false;
    return !(preface || '').toLowerCase().includes(field.id.toLowerCase());
}

// The offer as the customer gets it: the same offer with the disconnected fields dropped.
function connectedOffer({ offer, preface }) {
    return {
        ...offer,
        sections: (offer.sections || []).map(section => ({
            ...section,
            offers: (section.offers || []).map(sectionOffer => ({
                ...sectionOffer,
                fields: (sectionOffer.fields || []).filter(field => !isDisconnectedField({ section, field, preface })),
            })),
        })),
    };
}

// A quote that has been sent carries the rates it was priced at, and those are the rates its
// profit means anything in. Today's would quietly restate a figure the customer already has.
function effectiveExchangeRates({ opportunity, exchangeRates }) {
    const locked = opportunity?.exchangeRates;
    const hasLocked = locked?.live && Object.keys(locked.live).length > 0;
    return { rates: hasLocked ? locked.live : exchangeRates, lockedExchangeRates: locked };
}

function calcOfferSellAndCost({ offer, opportunity, exchangeRates, organization }) {
    const currency = organization?.defaults?.currency || 'USD';
    const { rates, lockedExchangeRates } = effectiveExchangeRates({ opportunity, exchangeRates });

    try {
        const priced = connectedOffer({ offer, preface: opportunity.preface || '' });

        const sums = calcOfferSum({
            offer: priced,
            products: opportunity.products,
            transportationMethod: opportunity.transportationMethod,
            buyingRates: opportunity.buyingRates,
            selected: opportunity.selected,
            exchangeRates: rates,
            lockedExchangeRates,
            organization,
        });
        const sell = (sums.min.normalized || 0) * (rates[currency] || 1);

        const costLines = (priced.sections || []).map(section =>
            (section.offers || []).map(sectionOffer => (sectionOffer.fields || []).map(
                field => getTotalRates({
                    field,
                    fieldBuyingRates: field.buyingRates,
                    products: opportunity.products,
                    buyingRates: opportunity.buyingRates,
                    transportationMethod: opportunity.transportationMethod,
                    exchangeRates: rates,
                    organization,
                })
            ))).flat().flat();
        const cost = _.sum(costLines) * (rates[currency] || 1);

        return { sell, cost, gp: sell - cost };
    } catch (e) {
        // A malformed offer must not take a whole table down with it.
        return { sell: 0, cost: 0, gp: 0 };
    }
}

// The offer a quote's money is counted from: the one picked, or the first when none is.
// The offer a quote is counted at: for a lost or rejected quote the offer it was lost on, when
// someone said which; otherwise the accepted (selected) offer; otherwise the first. `chosen` is
// false when a quote with several offers fell back to the first, so a report can say so.
function offerChoiceOf(opportunity) {
    const offers = opportunity?.offers || [];
    if (!offers.length) return { offer: null, index: -1, count: 0, chosen: false };
    const lost = ['lost', 'endcustomer-rejected'].includes(opportunity.status);
    let index = lost && opportunity.lostOffer ? offers.findIndex(o => o.id === opportunity.lostOffer) : -1;
    if (index < 0) index = offers.findIndex(o => opportunity.selected?.includes(o.id));
    const chosen = index >= 0 || offers.length === 1;
    if (index < 0) index = 0;
    return { offer: offers[index], index, count: offers.length, chosen };
}

function selectedOfferOf(opportunity) {
    return offerChoiceOf(opportunity).offer;
}

// What a quote sells for and costs, in the organization's currency, as its summary bar shows it.
// Every report that adds up quotes (analytics, tradeline targets, home KPIs, sales management,
// bookings) goes through here, so the same quote is the same amount on every screen. Null when
// the quote has no offer.
function opportunitySellAndCost({ opportunity, exchangeRates, organization }) {
    const offer = selectedOfferOf(opportunity);
    if (!offer) return null;
    return calcOfferSellAndCost({ offer, opportunity, exchangeRates, organization });
}

export { isDisconnectedField, connectedOffer, effectiveExchangeRates, calcOfferSellAndCost, offerChoiceOf, selectedOfferOf, opportunitySellAndCost };
